import React, { useState, useEffect, useCallback, useRef, memo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Pause, Play } from 'lucide-react';
import { Button } from '../components/ui/button';
import IntervalStaff from '../components/IntervalStaff';
import IntervalButtons from '../components/IntervalButtons';
import SessionTimer from '../components/SessionTimer';
import SessionCompleteOverlay from '../components/SessionCompleteOverlay';
import { useApp } from '../context/AppContext';
import { useAudio } from '../hooks/useAudio';
import { generateIntervalPair, INTERVALS } from '../hooks/useLocalStorage';

const SESSION_CONFIG = {
  timed: { duration: 60 },
  count: { totalCards: 20, warmupCards: 4 }
};

// All notes available for interval practice (full range that can be displayed)
// Treble clef: C4 to C6, Bass clef: C2 to C4
const INTERVAL_PRACTICE_NOTES = [
  // Bass clef notes
  'C2', 'D2', 'E2', 'F2', 'G2', 'A2', 'B2',
  'C3', 'D3', 'E3', 'F3', 'G3', 'A3', 'B3',
  // Treble clef notes (C4 is shared)
  'C4', 'D4', 'E4', 'F4', 'G4', 'A4', 'B4',
  'C5', 'D5', 'E5', 'F5', 'G5', 'A5', 'B5',
  'C6'
];

// Select next interval using spaced repetition
function selectNextInterval(intervalStats, recentIntervals = []) {
  const intervals = INTERVALS;
  
  // Calculate weights based on mastery (less mastered = higher weight)
  const weights = intervals.map(interval => {
    const stats = intervalStats[interval];
    if (!stats) return 1;
    
    // Higher weight for intervals that need more practice
    const total = stats.correctCount + stats.incorrectCount;
    if (total === 0) return 1.5; // Slightly prefer new intervals
    
    const accuracy = stats.correctCount / total;
    return 2 - accuracy; // Range: 1 (100% accurate) to 2 (0% accurate)
  });
  
  // Reduce weight for recently shown intervals
  const adjustedWeights = weights.map((weight, index) => {
    if (recentIntervals.includes(intervals[index])) {
      return weight * 0.3; // Strongly reduce chance of repetition
    }
    return weight;
  });
  
  // Weighted random selection
  const totalWeight = adjustedWeights.reduce((a, b) => a + b, 0);
  let random = Math.random() * totalWeight;
  
  for (let i = 0; i < intervals.length; i++) {
    random -= adjustedWeights[i];
    if (random <= 0) {
      return intervals[i];
    }
  }
  
  return intervals[0];
}

const IntervalPractice = memo(function IntervalPractice() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode') || 'timed';
  
  const { intervals, currentRange, settings, updateIntervalStats } = useApp();
  const { initAudio, playCorrectNote, playIncorrect, isLoaded: audioLoaded } = useAudio(settings.soundEnabled);
  
  // Session state
  const [currentPair, setCurrentPair] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(SESSION_CONFIG.timed.duration);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [sessionResults, setSessionResults] = useState([]);
  
  // Session complete overlay state
  const [showSessionComplete, setShowSessionComplete] = useState(false);
  const [sessionMetrics, setSessionMetrics] = useState(null);
  
  // Feedback state
  const [feedbackInterval, setFeedbackInterval] = useState(null);
  const [feedbackType, setFeedbackType] = useState(null);
  const [showAnswer, setShowAnswer] = useState(false);
  const [attemptCount, setAttemptCount] = useState(0);
  
  // Timing refs
  const noteStartTime = useRef(Date.now());
  const timerRef = useRef(null);
  const recentIntervalsRef = useRef([]);
  
  // Initialize audio on first interaction
  useEffect(() => {
    if (settings.soundEnabled && !audioLoaded) {
      initAudio();
    }
  }, [settings.soundEnabled, audioLoaded, initAudio]);
  
  // Generate next interval pair
  const getNextPair = useCallback((excludeInterval = null) => {
    const recent = recentIntervalsRef.current;
    
    // Select next interval to practice
    let targetInterval = selectNextInterval(intervals, recent);
    
    // Avoid immediate repetition
    if (excludeInterval && targetInterval === excludeInterval) {
      const otherIntervals = INTERVALS.filter(i => i !== excludeInterval);
      targetInterval = otherIntervals[Math.floor(Math.random() * otherIntervals.length)];
    }
    
    // Generate a pair for this interval using full note range
    const pair = generateIntervalPair(INTERVAL_PRACTICE_NOTES, targetInterval);
    
    if (pair) {
      // Update recent intervals buffer
      recentIntervalsRef.current = [...recent.slice(-2), targetInterval];
      return pair;
    }
    
    // If no valid pair for target interval, try any interval
    for (const interval of INTERVALS) {
      const fallbackPair = generateIntervalPair(INTERVAL_PRACTICE_NOTES, interval);
      if (fallbackPair) {
        recentIntervalsRef.current = [...recent.slice(-2), interval];
        return fallbackPair;
      }
    }
    
    return null;
  }, [intervals]);
  
  // Initialize first pair
  useEffect(() => {
    if (!currentPair && !isComplete) {
      const pair = getNextPair();
      if (pair) {
        setCurrentPair(pair);
        noteStartTime.current = Date.now();
      }
    }
  }, [currentPair, isComplete, getNextPair]);
  
  // Timer logic for timed mode
  useEffect(() => {
    if (mode !== 'timed' || isPaused || isComplete) return;
    
    timerRef.current = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          setIsComplete(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timerRef.current);
  }, [mode, isPaused, isComplete]);
  
  // Check for count mode completion
  useEffect(() => {
    if (mode === 'count' && currentCardIndex >= SESSION_CONFIG.count.totalCards) {
      setIsComplete(true);
    }
  }, [mode, currentCardIndex]);
  
  // Calculate session metrics
  const calculateMetrics = useCallback((results) => {
    const total = results.length;
    const correct = results.filter(r => r.isCorrect).length;
    const incorrect = total - correct;
    const accuracy = total > 0 ? (correct / total) * 100 : 0;
    const avgResponseTime = total > 0 
      ? results.reduce((sum, r) => sum + r.responseTime, 0) / total 
      : 0;
    
    return {
      totalCards: total,
      correctCount: correct,
      incorrectCount: incorrect,
      accuracy: accuracy.toFixed(1),
      avgResponseTime: Math.round(avgResponseTime)
    };
  }, []);
  
  // Show session complete overlay when session ends
  useEffect(() => {
    if (isComplete && !showSessionComplete) {
      const metrics = calculateMetrics(sessionResults);
      setSessionMetrics(metrics);
      setShowSessionComplete(true);
    }
  }, [isComplete, showSessionComplete, sessionResults, calculateMetrics]);
  
  // Handle session complete overlay continue
  const handleSessionCompleteContinue = useCallback(() => {
    sessionStorage.setItem('sessionResults', JSON.stringify({
      mode,
      type: 'intervals',
      results: sessionResults,
      metrics: sessionMetrics
    }));
    navigate('/summary');
  }, [mode, sessionResults, sessionMetrics, navigate]);
  
  // Handle interval selection
  const handleIntervalSelect = useCallback(async (selectedInterval) => {
    if (isPaused || isComplete || feedbackInterval) return;
    
    // Initialize audio on first user interaction (required for iOS)
    if (settings.soundEnabled && !audioLoaded) {
      await initAudio();
    }
    
    const responseTime = Date.now() - noteStartTime.current;
    const isCorrect = selectedInterval === currentPair.interval;
    
    if (isCorrect) {
      // Correct answer
      setFeedbackInterval(selectedInterval);
      setFeedbackType('correct');
      
      if (settings.soundEnabled) {
        // Play both notes of the interval simultaneously
        playCorrectNote(currentPair.lowerNote);
        playCorrectNote(currentPair.upperNote);
      }
      
      // Record result (only count first attempt as correct)
      const result = {
        interval: currentPair.interval,
        isCorrect: attemptCount === 0,
        responseTime,
        attempts: attemptCount + 1
      };
      setSessionResults(prev => [...prev, result]);
      
      // Update stats
      updateIntervalStats(currentPair.interval, attemptCount === 0, responseTime);
      
      // Move to next pair after delay
      setTimeout(() => {
        setFeedbackInterval(null);
        setFeedbackType(null);
        setShowAnswer(false);
        setAttemptCount(0);
        setCurrentPair(getNextPair(currentPair.interval));
        setCurrentCardIndex(prev => prev + 1);
        noteStartTime.current = Date.now();
      }, 600);
      
    } else {
      // Incorrect answer
      setFeedbackInterval(selectedInterval);
      setFeedbackType('incorrect');
      
      if (settings.soundEnabled) {
        playIncorrect();
      }
      
      if (attemptCount === 0) {
        // First wrong attempt - show feedback and allow second try
        setAttemptCount(1);
        setTimeout(() => {
          setFeedbackInterval(null);
          setFeedbackType(null);
        }, 400);
      } else {
        // Second wrong attempt - show correct answer and move on
        setShowAnswer(true);
        
        // Record as incorrect
        const result = {
          interval: currentPair.interval,
          isCorrect: false,
          responseTime,
          attempts: attemptCount + 1
        };
        setSessionResults(prev => [...prev, result]);
        updateIntervalStats(currentPair.interval, false, responseTime);
        
        // Move to next after showing answer
        setTimeout(() => {
          setFeedbackInterval(null);
          setFeedbackType(null);
          setShowAnswer(false);
          setAttemptCount(0);
          setCurrentPair(getNextPair(currentPair.interval));
          setCurrentCardIndex(prev => prev + 1);
          noteStartTime.current = Date.now();
        }, 1500);
      }
    }
  }, [isPaused, isComplete, feedbackInterval, currentPair, attemptCount, settings.soundEnabled, audioLoaded, initAudio, playCorrectNote, playIncorrect, updateIntervalStats, getNextPair]);
  
  // Handle pause/resume
  const togglePause = useCallback(() => {
    setIsPaused(prev => !prev);
  }, []);
  
  // Handle exit
  const handleExit = useCallback(() => {
    if (sessionResults.length > 0) {
      const confirmed = window.confirm('Exit session? Your progress will be saved.');
      if (!confirmed) return;
      
      const metrics = calculateMetrics(sessionResults);
      sessionStorage.setItem('sessionResults', JSON.stringify({
        mode,
        type: 'intervals',
        results: sessionResults,
        metrics,
        partial: true
      }));
      navigate('/summary');
    } else {
      navigate('/');
    }
  }, [navigate, mode, sessionResults, calculateMetrics]);

  if (!currentPair) {
    return (
      <div className="h-[100dvh] bg-cream-100 flex items-center justify-center">
        <div className="text-center px-6">
          <p className="text-cream-800 mb-4">No intervals available for the current note range.</p>
          <Button
            onClick={() => navigate('/')}
            className="bg-coral-500 hover:bg-coral-600 text-white font-sans font-medium rounded-btn"
          >
            Back to Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[100dvh] bg-cream-100 flex flex-col" data-testid="interval-practice-screen">
      {/* Header */}
      <header className="flex items-center justify-between px-4 py-2 sm:py-3 border-b border-wood-300 bg-cream-100/80 backdrop-blur-sm flex-shrink-0">
        <button
          onClick={handleExit}
          className="icon-btn w-10 h-10 rounded-full flex items-center justify-center transition-colors"
          style={{ backgroundColor: 'rgba(107, 127, 140, 0.1)' }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(107, 127, 140, 0.2)'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(107, 127, 140, 0.1)'}
          aria-label="Exit session"
          data-testid="exit-session-btn"
        >
          <X className="w-5 h-5" style={{ color: '#6B7F8C' }} strokeWidth={1.5} />
        </button>
        
        <SessionTimer
          mode={mode}
          timeRemaining={timeRemaining}
          currentCard={currentCardIndex}
          totalCards={SESSION_CONFIG.count.totalCards}
          isPaused={isPaused}
        />
        
        <button
          onClick={togglePause}
          className="icon-btn w-10 h-10 rounded-full flex items-center justify-center transition-colors"
          style={{ backgroundColor: 'rgba(107, 127, 140, 0.1)' }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(107, 127, 140, 0.2)'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(107, 127, 140, 0.1)'}
          aria-label={isPaused ? 'Resume' : 'Pause'}
          data-testid="pause-btn"
        >
          {isPaused ? (
            <Play className="w-5 h-5" style={{ color: '#6B7F8C' }} strokeWidth={1.5} />
          ) : (
            <Pause className="w-5 h-5" style={{ color: '#6B7F8C' }} strokeWidth={1.5} />
          )}
        </button>
      </header>
      
      {/* Main Practice Area */}
      <main className="flex-1 min-h-0 flex flex-col items-center justify-center px-4 py-2 relative overflow-visible">
        {/* Pause Overlay */}
        <AnimatePresence>
          {isPaused && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-cream-100/90 backdrop-blur-sm z-10 flex items-center justify-center"
            >
              <div className="text-center">
                <h2 className="font-serif text-2xl text-cream-900 mb-4">Paused</h2>
                <Button
                  onClick={togglePause}
                  className="bg-coral-500 hover:bg-coral-600 active:bg-coral-700 text-white font-sans font-medium rounded-btn transition-colors"
                  data-testid="resume-btn"
                >
                  <Play className="w-4 h-4 mr-2" />
                  Resume
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* Staff Display */}
        <AnimatePresence mode="wait">
          <motion.div
            key={`${currentPair.lowerNote}-${currentPair.upperNote}`}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="w-full max-w-2xl flex flex-col items-center justify-center"
          >
            <IntervalStaff
              lowerNote={currentPair.lowerNote}
              upperNote={currentPair.upperNote}
              showAnswer={showAnswer}
              correctInterval={currentPair.interval}
            />
          </motion.div>
        </AnimatePresence>
        
        {/* Attempt indicator */}
        {attemptCount > 0 && !showAnswer && (
          <motion.p
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-sm text-coral-500 font-sans mt-4"
          >
            Try again! One more chance.
          </motion.p>
        )}
      </main>
      
      {/* Interval Buttons Section */}
      <div className="flex-shrink-0">
        <IntervalButtons
          onSelect={handleIntervalSelect}
          feedbackInterval={feedbackInterval}
          feedbackType={feedbackType}
          disabled={isPaused || !!feedbackInterval || showSessionComplete}
        />
      </div>
      
      {/* Session Complete Overlay */}
      <SessionCompleteOverlay
        isVisible={showSessionComplete}
        metrics={sessionMetrics}
        mode={mode}
        sessionType="intervals"
        masteredNotes={[]}
        onContinue={handleSessionCompleteContinue}
      />
    </div>
  );
});

export default IntervalPractice;
